import React, {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { Formik, Form, getIn } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import { selectUser } from "../../redux/modules/userSlice";
import { UserAPI } from "../api";
import useDraft from "../../hooks/useDraft";
import {
  applySectionDraftData,
  pickSectionDraftData,
} from "../../drafts/draftHelpers";
import {
  applyHrLockedFields,
  isHrLockedField,
  pickHrOnboardingSnapshot,
} from "../../utils/hrFieldAccess";
import SelectSkills from "../React Select/SelectSkills";
import { MediaPreviewDialog, MediaViewActions } from "../Shared/MediaPreview";
import {
  checkInputRule,
  ruleSchema,
  validateDraft,
  notifyFormInvalid,
} from "../../utils/inputRules";
import {
  BLOOD_GROUPS,
  GENDER_OPTIONS,
  MARITAL_OPTIONS,
  RELATION_OPTIONS,
  NATURE_OF_RESIDENCY,
  FOOD_COUPON_OPTIONS,
  FIELD_HINTS,
  SIGNATURE_LEGAL_TEXT,
  emptyContactRow,
  EXPERIENCE_YEAR_OPTIONS,
  EXPERIENCE_MONTH_OPTIONS,
  JOINEE_FORM_COLORS,
  JOINEE_FORM_FONT,
} from "../../config/joineeFormConstants";
import {
  FormTextField,
  FormSelectField,
  FormRadioField,
  FormCheckboxField,
  FormDateField,
  FormTwoCol,
  FormSectionTitle,
  FormAddButton,
  FormFieldLabel,
} from "./JoineeFormFields";
import signaturePlaceholder from "../../Assests/Images/joinee/form/signature-placeholder.png";

var DRAFT_SECTION = "A";

var bloodGroupOptions = BLOOD_GROUPS.map(function (bg) {
  return { value: bg, label: bg };
});

var yesNoOptions = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

function emptyAddress() {
  return {
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    landmark: "",
    postalCode: "",
    periodOfStay: "",
    natureOfResidency: "",
  };
}

function getDefaultValues() {
  return {
    _id: "",
    name: "",
    email: "",
    designation: "",
    doj: "",
    workLocation: "",
    aadhaar: "",
    pan: "",
    companyName: "",
    phoneNumber: "",
    secondaryEmail: "",
    altPhoneNumber: "",
    mother: { name: "", occupation: "" },
    father: { name: "", occupation: "" },
    gender: "Male",
    dateOfBirth: null,
    bloodGroup: "",
    maritalStatus: "Married",
    religion: "",
    isPassport: "No",
    passport: "",
    passportExpiry: null,
    hasPriorExperience: true,
    totalExperienceInYears: "",
    totalExperienceInMonths: "",
    relevantExperienceInYears: "",
    relevantExperienceInMonths: "",
    linkedIn: "",
    pf: "",
    uan: "",
    ESIC: "",
    skills: [],
    currentAddress: emptyAddress(),
    permanentAddress: emptyAddress(),
    permanentAddressSame: false,
    familyDetails: [emptyContactRow()],
    emergencyContact: [emptyContactRow()],
    isSodexo: false,
    sodexoCouponValue: "",
    signature: "",
    signature_fileName: "",
    profilePhoto: "",
    profilePhoto_fileName: "",
    signatureLegalConsent: false,
    signatureLegalConsentAt: null,
  };
}

function normalizeMobile(value) {
  if (!value) {
    return "";
  }
  var digits = String(value).replace(/\D/g, "");
  if (digits.length === 12 && digits.indexOf("91") === 0) {
    return digits.slice(2);
  }
  if (digits.length === 11 && digits.charAt(0) === "0") {
    return digits.slice(1);
  }
  return digits;
}

function isFutureDate(value) {
  if (!value) {
    return false;
  }
  var d = new Date(value);
  if (isNaN(d.getTime())) {
    return false;
  }
  var now = new Date();
  return d.getTime() > now.getTime();
}

function applyExperienceDerived(values) {
  var next = Object.assign({}, values);
  if (!values.hasPriorExperience) {
    next.totalExperienceInYears = "";
    next.totalExperienceInMonths = "";
    next.relevantExperienceInYears = "";
    next.relevantExperienceInMonths = "";
  }
  return next;
}

function experienceToMonths(years, months) {
  var y = years === "25+" ? 25 : parseInt(years, 10);
  var m = parseInt(months, 10);
  if (isNaN(y)) {
    y = 0;
  }
  if (isNaN(m)) {
    m = 0;
  }
  return y * 12 + m;
}

function mapIsPassportToUi(raw) {
  if (raw === true || raw === "true" || raw === "Yes" || raw === "yes") {
    return "Yes";
  }
  if (raw === false || raw === "false" || raw === "No" || raw === "no") {
    return "No";
  }
  return "No";
}

function mapIsPassportToApi(value) {
  return value === "Yes" || value === true || value === "true";
}

function mapGenderToUi(raw) {
  if (raw === null || typeof raw === "undefined" || raw === "") {
    return "";
  }
  var lower = String(raw).trim().toLowerCase();
  if (lower === "male" || lower === "m") {
    return "Male";
  }
  if (lower === "female" || lower === "f") {
    return "Female";
  }
  if (
    lower === "prefer not to answer" ||
    lower === "na" ||
    lower === "n/a" ||
    lower === "other" ||
    lower === "prefer_not_to_answer"
  ) {
    return "Prefer Not to Answer";
  }
  if (raw === "Male" || raw === "Female" || raw === "Prefer Not to Answer") {
    return raw;
  }
  return "";
}

function mapMaritalToUi(raw) {
  if (raw === true || raw === "true") {
    return "Married";
  }
  if (raw === false || raw === "false") {
    return "Unmarried";
  }
  if (typeof raw === "string" && raw) {
    var lower = raw.toLowerCase().trim();
    if (lower === "married") {
      return "Married";
    }
    if (
      lower === "unmarried" ||
      lower === "un married" ||
      lower === "single" ||
      lower === "not married"
    ) {
      return "Unmarried";
    }
    if (lower === "widow") {
      return "Widow";
    }
    if (lower === "widower") {
      return "Widower";
    }
    if (lower === "divorcee" || lower === "divorced") {
      return "Divorcee";
    }
    return "";
  }
  return "";
}

function mapUserToFormValues(rawUser) {
  var raw = rawUser && typeof rawUser === "object" ? rawUser : {};
  var defaults = getDefaultValues();
  var currentAddress =
    raw.currentAddress && typeof raw.currentAddress === "object"
      ? Object.assign({}, emptyAddress(), raw.currentAddress)
      : emptyAddress();
  var permanentAddress =
    raw.permanentAddress && typeof raw.permanentAddress === "object"
      ? Object.assign({}, emptyAddress(), raw.permanentAddress)
      : emptyAddress();

  var familyDetails =
    Array.isArray(raw.familyDetails) && raw.familyDetails.length > 0
      ? raw.familyDetails.map(function (row) {
          return {
            name: row && row.name ? row.name : "",
            contact: row && row.contact ? row.contact : "",
            relationship: row && row.relationship ? row.relationship : "",
          };
        })
      : [emptyContactRow()];

  var emergencyContact =
    Array.isArray(raw.emergencyContact) && raw.emergencyContact.length > 0
      ? raw.emergencyContact.map(function (row) {
          return {
            name: row && row.name ? row.name : "",
            contact: row && row.contact ? row.contact : "",
            relationship: row && row.relationship ? row.relationship : "",
          };
        })
      : [emptyContactRow()];

  var hasPrior =
    typeof raw.hasPriorExperience === "boolean"
      ? raw.hasPriorExperience
      : typeof raw.isFresher === "boolean"
        ? raw.isFresher
        : defaults.hasPriorExperience;

  return {
    _id: raw._id ? raw._id : "",
    name: raw.name ? raw.name : "",
    email: raw.email ? raw.email : "",
    designation: raw.designation ? raw.designation : "",
    doj: raw.doj ? raw.doj : "",
    workLocation: raw.workLocation ? raw.workLocation : "",
    aadhaar: raw.aadhaar ? raw.aadhaar : "",
    pan: raw.pan ? raw.pan : "",
    companyName: raw.companyName ? raw.companyName : "",
    phoneNumber: raw.phoneNumber ? raw.phoneNumber : "",
    secondaryEmail: raw.secondaryEmail ? raw.secondaryEmail : "",
    altPhoneNumber: raw.altPhoneNumber ? raw.altPhoneNumber : "",
    mother:
      raw.mother && typeof raw.mother === "object"
        ? {
            name: raw.mother.name ? raw.mother.name : "",
            occupation: raw.mother.occupation ? raw.mother.occupation : "",
          }
        : { name: "", occupation: "" },
    father:
      raw.father && typeof raw.father === "object"
        ? {
            name: raw.father.name ? raw.father.name : "",
            occupation: raw.father.occupation ? raw.father.occupation : "",
          }
        : { name: "", occupation: "" },
    gender: mapGenderToUi(raw.gender),
    dateOfBirth: raw.dateOfBirth ? raw.dateOfBirth : null,
    bloodGroup: raw.bloodGroup ? raw.bloodGroup : "",
    maritalStatus: mapMaritalToUi(raw.maritalStatus),
    religion: raw.religion ? raw.religion : "",
    isPassport: mapIsPassportToUi(raw.isPassport),
    passport: raw.passport ? raw.passport : "",
    passportExpiry: raw.passportExpiry ? raw.passportExpiry : null,
    hasPriorExperience: hasPrior,
    totalExperienceInYears: raw.totalExperienceInYears
      ? String(raw.totalExperienceInYears)
      : "",
    totalExperienceInMonths: raw.totalExperienceInMonths
      ? String(raw.totalExperienceInMonths)
      : "",
    relevantExperienceInYears: raw.relevantExperienceInYears
      ? String(raw.relevantExperienceInYears)
      : "",
    relevantExperienceInMonths: raw.relevantExperienceInMonths
      ? String(raw.relevantExperienceInMonths)
      : "",
    linkedIn: raw.linkedIn ? raw.linkedIn : "",
    pf: raw.pf ? raw.pf : "",
    uan: raw.uan ? raw.uan : "",
    ESIC: raw.ESIC ? raw.ESIC : "",
    skills: Array.isArray(raw.skills) ? raw.skills : [],
    currentAddress: currentAddress,
    permanentAddress: permanentAddress,
    permanentAddressSame: Boolean(raw.permanentAddressSame),
    familyDetails: familyDetails,
    emergencyContact: emergencyContact,
    isSodexo: Boolean(raw.isSodexo),
    sodexoCouponValue: raw.sodexoCouponValue
      ? String(raw.sodexoCouponValue)
      : "",
    signature: raw.signature ? raw.signature : "",
    signature_fileName: raw.signature_fileName ? raw.signature_fileName : "",
    profilePhoto: raw.profilePhoto ? raw.profilePhoto : "",
    profilePhoto_fileName: raw.profilePhoto_fileName
      ? raw.profilePhoto_fileName
      : "",
    signatureLegalConsent: Boolean(raw.signatureLegalConsent),
    signatureLegalConsentAt: raw.signatureLegalConsentAt
      ? raw.signatureLegalConsentAt
      : null,
  };
}

function contactRowSchema() {
  return Yup.object({
    name: ruleSchema("personName", { label: "Name", required: true }),
    contact: ruleSchema("mobile", {
      label: "Phone number",
      required: true,
    }),
    relationship: Yup.string().trim().required("Relation is required"),
  });
}

function addressSchema(required) {
  if (!required) {
    return Yup.object({
      addressLine1: Yup.string().nullable(),
      addressLine2: Yup.string().nullable(),
      addressLine3: Yup.string().nullable(),
      landmark: Yup.string().nullable(),
      postalCode: Yup.string().nullable(),
      periodOfStay: Yup.string().nullable(),
      natureOfResidency: Yup.string().nullable(),
    });
  }
  return Yup.object({
    addressLine1: ruleSchema("address", {
      label: "Address Line 1",
      required: true,
    }),
    addressLine2: ruleSchema("address", { label: "Address Line 2" }),
    addressLine3: ruleSchema("address", { label: "Address Line 3" }),
    landmark: ruleSchema("address", { label: "Landmark" }),
    postalCode: ruleSchema("postalCode", {
      label: "Postal code",
      required: true,
    }),
    periodOfStay: ruleSchema("years", {
      label: "Period of stay",
      required: true,
    }),
    natureOfResidency: Yup.string()
      .trim()
      .required("Nature of residency is required"),
  });
}

function buildValidationSchema(requireConsent, lockedFields) {
  var locked = lockedFields || {};
  return Yup.object({
    secondaryEmail: ruleSchema("email", { label: "Secondary mail" }),
    altPhoneNumber: ruleSchema("mobile", { label: "Alternative phone number" }),
    mother: Yup.object({
      name: ruleSchema("personName", {
        label: "Mother's name",
        required: true,
      }),
      occupation: ruleSchema("occupation", {
        label: "Mother's occupation",
        required: true,
      }),
    }),
    father: Yup.object({
      name: ruleSchema("personName", {
        label: "Father's name",
        required: true,
      }),
      occupation: ruleSchema("occupation", {
        label: "Father's occupation",
        required: true,
      }),
    }),
    dateOfBirth: Yup.mixed()
      .required("Date of birth is required")
      .test("not-future", "Date of birth cannot be in the future", function (
        val
      ) {
        return !isFutureDate(val);
      }),
    bloodGroup: Yup.string().trim().required("Blood group is required"),
    gender: Yup.string().trim().required("Gender is required"),
    maritalStatus: Yup.string().trim().required("Marital status is required"),
    religion: ruleSchema("place", { label: "Religion", required: true }),
    isPassport: Yup.string()
      .oneOf(["Yes", "No"])
      .required("Please select passport status"),
    passport: Yup.string().when("isPassport", {
      is: "Yes",
      then: function (schema) {
        return schema
          .trim()
          .required("Passport number is required")
          .test("passport-format", "", function (val) {
            var message = checkInputRule("passport", val);
            return message ? this.createError({ message: message }) : true;
          });
      },
      otherwise: function (schema) {
        return schema.nullable();
      },
    }),
    passportExpiry: Yup.mixed().when("isPassport", {
      is: "Yes",
      then: function (schema) {
        return schema.required("Passport expiry date is required");
      },
      otherwise: function (schema) {
        return schema.nullable();
      },
    }),
    hasPriorExperience: Yup.boolean(),
    totalExperienceInYears: Yup.string().when("hasPriorExperience", {
      is: true,
      then: function (schema) {
        return schema.required("Total experience year is required");
      },
      otherwise: function (schema) {
        return schema.nullable();
      },
    }),
    totalExperienceInMonths: Yup.string().when("hasPriorExperience", {
      is: true,
      then: function (schema) {
        return schema.required("Total experience month is required");
      },
      otherwise: function (schema) {
        return schema.nullable();
      },
    }),
    relevantExperienceInYears: Yup.string().when("hasPriorExperience", {
      is: true,
      then: function (schema) {
        return schema.required("Relevant experience year is required");
      },
      otherwise: function (schema) {
        return schema.nullable();
      },
    }),
    relevantExperienceInMonths: Yup.string().when("hasPriorExperience", {
      is: true,
      then: function (schema) {
        return schema
          .required("Relevant experience month is required")
          .test(
            "relevant-lte-total",
            "Relevant experience cannot exceed total experience",
            function () {
              var parent = this.parent;
              if (!parent.hasPriorExperience) {
                return true;
              }
              return (
                experienceToMonths(
                  parent.relevantExperienceInYears,
                  parent.relevantExperienceInMonths
                ) <=
                experienceToMonths(
                  parent.totalExperienceInYears,
                  parent.totalExperienceInMonths
                )
              );
            }
          );
      },
      otherwise: function (schema) {
        return schema.nullable();
      },
    }),
    linkedIn: ruleSchema("linkedIn", { label: "LinkedIn profile URL" }),
    pf: locked.pf
      ? Yup.string().nullable()
      : ruleSchema("pf", { label: "PF number" }),
    uan: locked.uan
      ? Yup.string().nullable()
      : ruleSchema("uan", { label: "UAN" }),
    ESIC: ruleSchema("esic", { label: "ESIC number" }),
    skills: Yup.array()
      .min(1, "Please select at least one skill")
      .test("skill-format", "Invalid skill", function (list) {
        var bad = (list || []).find(function (skill) {
          return checkInputRule("skill", skill, "Skill");
        });
        return bad
          ? this.createError({
              message: '"' + bad + '": ' + checkInputRule("skill", bad, "Skill"),
            })
          : true;
      }),
    currentAddress: addressSchema(true),
    permanentAddressSame: Yup.boolean(),
    permanentAddress: Yup.object().when("permanentAddressSame", {
      is: true,
      then: function () {
        return addressSchema(false);
      },
      otherwise: function () {
        return addressSchema(true);
      },
    }),
    familyDetails: Yup.array().of(contactRowSchema()).min(1),
    emergencyContact: Yup.array().of(contactRowSchema()).min(1),
    sodexoCouponValue: Yup.string().nullable(),
    signature: Yup.string().required("Signature is required"),
    signatureLegalConsent: requireConsent
      ? Yup.boolean().oneOf(
          [true],
          "Please accept the signature legal consent"
        )
      : Yup.boolean(),
  });
}

function errMsg(formik, path) {
  var touched = getIn(formik.touched, path);
  var error = getIn(formik.errors, path);
  if (touched && error && typeof error === "string") {
    return error;
  }
  return "";
}

function setBusy(onBusyChange, value) {
  if (typeof onBusyChange === "function") {
    onBusyChange(value || false);
  }
}

/** Matches FormFieldLabel height (14px * 1.14 line + 6px mb) so icon sits on input row. */
var ROW_LABEL_SPACER_PX = Math.round(14 * 1.14 + 6);

function RowRemoveButton(props) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: 40,
        flexShrink: 0,
        alignSelf: "start",
      }}
    >
      <Box
        aria-hidden
        sx={{
          height: props.showLabelSpacer ? ROW_LABEL_SPACER_PX : 0,
          width: "100%",
          flexShrink: 0,
        }}
      />
      <IconButton
        type="button"
        aria-label={props["aria-label"] || "Remove row"}
        onClick={props.onClick}
        size="small"
        sx={{
          width: 36,
          height: 36,
          p: 0,
          color: JOINEE_FORM_COLORS.error,
          border: "1px solid " + JOINEE_FORM_COLORS.border,
          borderRadius: "6px",
          bgcolor: "#FDFDFD",
          "&:hover": {
            bgcolor: "#FEF2F2",
            borderColor: JOINEE_FORM_COLORS.error,
            color: JOINEE_FORM_COLORS.error,
          },
        }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
}

function CandidateInfo(props) {
  var changeTab = props.changeTab;
  var handleButtonClick = props.handleButtonClick;
  var setNewJoineeFlagButton = props.setNewJoineeFlagButton;
  var hideInternalActions = props.hideInternalActions;
  var formikRef = props.formikRef;
  var onBusyChange = props.onBusyChange;
  var readOnly = Boolean(props.readOnly);

  var reduxUser = useSelector(selectUser);
  var params = useParams();
  var location = useLocation();
  var id = params.id ? params.id : "";
  var userId =
    reduxUser && reduxUser.user && reduxUser.user.isAdmin
      ? id
      : reduxUser && reduxUser.user
        ? reduxUser.user._id
        : "";

  var draftApi = useDraft("newJoinee", DRAFT_SECTION);
  var loadDraft = draftApi.loadDraft;
  var saveDraft = draftApi.saveDraft;
  var deleteDraft = draftApi.deleteDraft;

  var [initialValues, setInitialValues] = useState(getDefaultValues());
  var [formKey, setFormKey] = useState(0);
  var [loading, setLoading] = useState(true);
  var [signaturePreviewOpen, setSignaturePreviewOpen] = useState(false);
  var [hrOnboardingSnapshot, setHrOnboardingSnapshot] = useState({});
  var formikInstanceRef = useRef(null);
  var signatureInputRef = useRef(null);
  var isAdmin =
    reduxUser && reduxUser.user ? Boolean(reduxUser.user.isAdmin) : false;
  var lockedFields = {
    pf: isHrLockedField(isAdmin, hrOnboardingSnapshot.pf),
    uan: isHrLockedField(isAdmin, hrOnboardingSnapshot.uan),
  };

  useEffect(
    function () {
      var isActive = true;

      function finishLoad(values) {
        if (!isActive) {
          return;
        }
        setInitialValues(values);
        setFormKey(function (k) {
          return k + 1;
        });
        setLoading(false);
      }

      setLoading(true);

      function loadFromApi(snapshotOnly) {
        UserAPI.getUser(userId)
          .then(function (res) {
            if (!isActive) {
              return;
            }
            var data = res && res.data ? res.data : {};
            var snapshot = pickHrOnboardingSnapshot(data);
            setHrOnboardingSnapshot(snapshot);
            if (snapshotOnly) {
              return snapshot;
            }
            if (typeof setNewJoineeFlagButton === "function") {
              setNewJoineeFlagButton(
                data.isNewJoineeFormComplete
                  ? data.isNewJoineeFormComplete
                  : ""
              );
            }
            finishLoad(
              applyHrLockedFields(
                mapUserToFormValues(data),
                snapshot,
                isAdmin
              )
            );
          })
          .catch(function (err) {
            console.log(err);
            if (isActive) {
              setLoading(false);
              toast.error("Unable to load candidate info");
            }
          });
      }

      if (!isAdmin) {
        UserAPI.getUser(userId)
          .then(function (res) {
            if (!isActive) {
              return null;
            }
            var data = res && res.data ? res.data : {};
            var snapshot = pickHrOnboardingSnapshot(data);
            setHrOnboardingSnapshot(snapshot);
            if (typeof setNewJoineeFlagButton === "function") {
              setNewJoineeFlagButton(
                data.isNewJoineeFormComplete
                  ? data.isNewJoineeFormComplete
                  : ""
              );
            }
            return snapshot;
          })
          .then(function (snapshot) {
            if (!isActive) {
              return;
            }
            return loadDraft()
              .then(function (loadedDraft) {
                if (!isActive) {
                  return;
                }
                var draftData =
                  loadedDraft && loadedDraft.data
                    ? loadedDraft.data
                    : loadedDraft;
                if (draftData && typeof draftData === "object") {
                  var applied = applySectionDraftData(
                    "newJoinee",
                    DRAFT_SECTION,
                    getDefaultValues(),
                    draftData
                  );
                  finishLoad(
                    applyHrLockedFields(
                      mapUserToFormValues(applied),
                      snapshot || {},
                      isAdmin
                    )
                  );
                  return;
                }
                loadFromApi(false);
              })
              .catch(function () {
                loadFromApi(false);
              });
          })
          .catch(function () {
            loadFromApi(false);
          });
      } else {
        loadFromApi(false);
      }

      return function () {
        isActive = false;
      };
    },
    [userId, isAdmin, loadDraft, setNewJoineeFlagButton, location.pathname]
  );

  function buildApiPayload(values) {
    var withExp = applyExperienceDerived(values);
    var permanent = withExp.permanentAddressSame
      ? Object.assign({}, withExp.currentAddress)
      : withExp.permanentAddress;
    var consentAccepted = Boolean(withExp.signatureLegalConsent);

    var payload = Object.assign({}, withExp, {
      isPassport: mapIsPassportToApi(withExp.isPassport),
      isFresher: Boolean(withExp.hasPriorExperience),
      maritalStatus: withExp.maritalStatus,
      permanentAddress: permanent,
      signatureLegalConsent: consentAccepted,
      signatureLegalConsentAt: consentAccepted
        ? withExp.signatureLegalConsentAt || new Date().toISOString()
        : null,
      passport:
        withExp.isPassport === "Yes" ? withExp.passport : "",
      passportExpiry:
        withExp.isPassport === "Yes" ? withExp.passportExpiry : null,
      altPhoneNumber: withExp.altPhoneNumber
        ? normalizeMobile(withExp.altPhoneNumber)
        : "",
      familyDetails: (withExp.familyDetails || []).map(function (row) {
        return {
          name: row.name,
          contact: normalizeMobile(row.contact),
          relationship: row.relationship,
        };
      }),
      emergencyContact: (withExp.emergencyContact || []).map(function (row) {
        return {
          name: row.name,
          contact: normalizeMobile(row.contact),
          relationship: row.relationship,
        };
      }),
    });
    return applyHrLockedFields(payload, hrOnboardingSnapshot, isAdmin);
  }

  function handleSaveDraft(formik) {
    if (isAdmin) {
      return Promise.resolve();
    }
    return validateDraft(
      formik,
      buildValidationSchema(false, lockedFields)
    ).then(function (ok) {
      return ok ? persistDraft(formik) : null;
    });
  }

  function persistDraft(formik) {
    var values = formik.values;
    var withExp = applyExperienceDerived(values);
    var consentAccepted = Boolean(withExp.signatureLegalConsent);
    var draftValues = Object.assign({}, withExp, {
      signatureLegalConsent: consentAccepted,
      signatureLegalConsentAt: consentAccepted
        ? withExp.signatureLegalConsentAt || new Date().toISOString()
        : null,
    });
    var wrapped = pickSectionDraftData("newJoinee", DRAFT_SECTION, draftValues);
    setBusy(onBusyChange, "draft");
    return saveDraft(wrapped)
      .then(function (saved) {
        setBusy(onBusyChange, false);
        if (saved && saved.skipped) {
          toast.info("Nothing to save as draft");
          return saved;
        }
        if (saved) {
          if (consentAccepted && !values.signatureLegalConsentAt) {
            formik.setFieldValue(
              "signatureLegalConsentAt",
              draftValues.signatureLegalConsentAt
            );
          }
          toast.success("Draft saved");
          return saved;
        }
        toast.error("Unable to save draft");
        return null;
      })
      .catch(function () {
        setBusy(onBusyChange, false);
        toast.error("Unable to save draft");
        return null;
      });
  }

  function handleSaveAndNext(values, formikHelpers) {
    var payload = buildApiPayload(values);
    setBusy(onBusyChange, "next");
    if (typeof handleButtonClick === "function") {
      handleButtonClick(
        "sectionA",
        !values.hasPriorExperience ? "Fresher" : ""
      );
    }

    UserAPI.updateUser(userId, payload)
      .then(function () {
        var afterDelete = isAdmin
          ? Promise.resolve(true)
          : deleteDraft();
        return afterDelete.then(function () {
          setBusy(onBusyChange, false);
          if (formikHelpers && formikHelpers.setSubmitting) {
            formikHelpers.setSubmitting(false);
          }
          if (typeof changeTab === "function") {
            changeTab(values.hasPriorExperience ? 1 : 2);
          }
        });
      })
      .catch(function (err) {
        console.log(err);
        setBusy(onBusyChange, false);
        if (formikHelpers && formikHelpers.setSubmitting) {
          formikHelpers.setSubmitting(false);
        }
        toast.error("Unable to save candidate info");
      });
  }

  useImperativeHandle(
    formikRef,
    function () {
      return {
        submitDraft: function () {
          var formik = formikInstanceRef.current;
          if (!formik || readOnly) {
            return Promise.resolve();
          }
          return handleSaveDraft(formik);
        },
        submitNext: function () {
          var formik = formikInstanceRef.current;
          if (!formik || readOnly) {
            return Promise.resolve();
          }
          return formik.validateForm().then(function (errors) {
            notifyFormInvalid(errors);
            return formik.submitForm();
          });
        },
        getFormik: function () {
          return formikInstanceRef.current;
        },
      };
    },
    [isAdmin, userId, onBusyChange, changeTab, handleButtonClick, readOnly]
  );

  if (loading) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography
          sx={{
            fontFamily: JOINEE_FORM_FONT,
            fontSize: 14,
            fontWeight: 400,
            color: JOINEE_FORM_COLORS.placeholder,
          }}
        >
          Loading candidate info…
        </Typography>
      </Box>
    );
  }

  return (
    <Formik
      key={formKey}
      initialValues={initialValues}
      enableReinitialize
      validationSchema={buildValidationSchema(true, lockedFields)}
      onSubmit={handleSaveAndNext}
    >
      {function (formik) {
        formikInstanceRef.current = formik;
        var values = formik.values;

        function onFieldChange(e) {
          formik.handleChange(e);
        }

        function onSameAddressChange(e) {
          var checked = e.target.checked;
          formik.setFieldValue("permanentAddressSame", checked);
          if (checked) {
            formik.setFieldValue(
              "permanentAddress",
              Object.assign({}, values.currentAddress)
            );
          }
        }

        function syncPermanentIfNeeded(path, value) {
          formik.setFieldValue(path, value);
          if (values.permanentAddressSame && path.indexOf("currentAddress.") === 0) {
            var key = path.replace("currentAddress.", "");
            formik.setFieldValue("permanentAddress." + key, value);
          }
        }

        function addFamilyRow() {
          var next = (values.familyDetails || []).concat([emptyContactRow()]);
          formik.setFieldValue("familyDetails", next);
        }

        function removeFamilyRow(index) {
          if ((values.familyDetails || []).length <= 1) {
            return;
          }
          var next = values.familyDetails.filter(function (_row, i) {
            return i !== index;
          });
          formik.setFieldValue("familyDetails", next);
        }

        function addEmergencyRow() {
          var next = (values.emergencyContact || []).concat([
            emptyContactRow(),
          ]);
          formik.setFieldValue("emergencyContact", next);
        }

        function removeEmergencyRow(index) {
          if ((values.emergencyContact || []).length <= 1) {
            return;
          }
          var next = values.emergencyContact.filter(function (_row, i) {
            return i !== index;
          });
          formik.setFieldValue("emergencyContact", next);
        }

        function setSkillsUser(updater) {
          var prev = { skills: values.skills || [] };
          var next =
            typeof updater === "function" ? updater(prev) : updater;
          var rawSkills =
            next && Array.isArray(next.skills) ? next.skills : [];
          var seen = {};
          var unique = [];
          rawSkills.forEach(function (skill) {
            var trimmed = String(skill || "").trim();
            var key = trimmed.toLowerCase();
            if (!key || seen[key]) {
              return;
            }
            seen[key] = true;
            unique.push(trimmed);
          });
          formik.setFieldValue("skills", unique);
          formik.setFieldTouched("skills", true, false);
        }

        function onSignatureFile(e) {
          var file =
            e.target.files && e.target.files[0] ? e.target.files[0] : null;
          if (!file) {
            return;
          }
          if (file.size > 500 * 1024) {
            toast.error("File size exceeds the 500 KB limit", {
              hideProgressBar: true,
              closeOnClick: true,
            });
            return;
          }
          var reader = new FileReader();
          reader.onload = function (ev) {
            var dataUrl = ev.target.result;
            formik.setFieldValue("signature", dataUrl);
            formik.setFieldValue("signature_fileName", file.name);
            formik.setFieldTouched("signature", true, false);
          };
          reader.readAsDataURL(file);
          e.target.value = "";
        }

        return (
          <Form noValidate>
            <Box
              component="fieldset"
              disabled={readOnly}
              sx={{
                border: 0,
                m: 0,
                p: 0,
                minWidth: 0,
                pointerEvents: readOnly ? "none" : "auto",
              }}
            >
            {/* 1. Additional Information */}
            <FormSectionTitle>Additional Information</FormSectionTitle>

            <FormTwoCol>
              <FormTextField
                id="ci-secondaryEmail"
                rule="email"
                name="secondaryEmail"
                label="Secondary Mail (If Any)"
                placeholder="e.g. name@example.com"
                value={values.secondaryEmail}
                onChange={onFieldChange}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "secondaryEmail")}
              />
              <FormTextField
                id="ci-altPhoneNumber"
                rule="mobile"
                name="altPhoneNumber"
                label="Alternative Phone Number"
                placeholder="e.g. 9876543210"
                value={values.altPhoneNumber}
                onChange={onFieldChange}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "altPhoneNumber")}
              />
            </FormTwoCol>

            <FormTwoCol>
              <FormTextField
                id="ci-mother-name"
                rule="personName"
                name="mother.name"
                label="Mother's Name"
                placeholder="Enter Name"
                required
                value={values.mother.name}
                onChange={onFieldChange}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "mother.name")}
              />
              <FormTextField
                id="ci-mother-occupation"
                rule="occupation"
                name="mother.occupation"
                label="Mother's Occupation"
                placeholder="Enter Occupation"
                required
                value={values.mother.occupation}
                onChange={onFieldChange}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "mother.occupation")}
              />
            </FormTwoCol>

            <FormTwoCol>
              <FormTextField
                id="ci-father-name"
                rule="personName"
                name="father.name"
                label="Father's Name"
                placeholder="Enter Name"
                required
                value={values.father.name}
                onChange={onFieldChange}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "father.name")}
              />
              <FormTextField
                id="ci-father-occupation"
                rule="occupation"
                name="father.occupation"
                label="Father's Occupation"
                placeholder="Enter Occupation"
                required
                value={values.father.occupation}
                onChange={onFieldChange}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "father.occupation")}
              />
            </FormTwoCol>

            <FormTwoCol>
              <FormDateField
                id="ci-dateOfBirth"
                name="dateOfBirth"
                label="Date of Birth"
                placeholder="Select Date of Birth"
                required
                disableFuture
                value={values.dateOfBirth}
                onChange={function (iso) {
                  formik.setFieldValue("dateOfBirth", iso);
                }}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "dateOfBirth")}
              />
              <FormSelectField
                id="ci-bloodGroup"
                name="bloodGroup"
                label="Blood Group"
                placeholder="Select Blood Group"
                required
                options={bloodGroupOptions}
                value={values.bloodGroup}
                onChange={onFieldChange}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "bloodGroup")}
              />
            </FormTwoCol>

            <FormTwoCol>
              <FormRadioField
                name="gender"
                label="Gender"
                required
                options={GENDER_OPTIONS}
                value={values.gender}
                onChange={onFieldChange}
                error={errMsg(formik, "gender")}
              />
              <FormTextField
                id="ci-religion"
                rule="place"
                name="religion"
                label="Religion"
                placeholder="Enter Religion"
                required
                value={values.religion}
                onChange={onFieldChange}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "religion")}
              />
            </FormTwoCol>

            <FormTwoCol>
              <FormRadioField
                name="maritalStatus"
                label="Marital Status"
                required
                options={MARITAL_OPTIONS}
                value={values.maritalStatus}
                onChange={onFieldChange}
                error={errMsg(formik, "maritalStatus")}
              />
              <FormRadioField
                name="isPassport"
                label="Do you have Passport"
                required
                options={yesNoOptions}
                value={values.isPassport}
                onChange={function (e) {
                  formik.handleChange(e);
                  if (e.target.value !== "Yes") {
                    formik.setFieldValue("passport", "");
                    formik.setFieldValue("passportExpiry", null);
                  }
                }}
                error={errMsg(formik, "isPassport")}
              />
            </FormTwoCol>

            {values.isPassport === "Yes" ? (
              <FormTwoCol>
                <FormTextField
                  id="ci-passport"
                  rule="passport"
                  name="passport"
                  label="Passport Number"
                  hint="1 letter followed by 7 digits, e.g. A1234567"
                  required
                  value={values.passport}
                  onChange={onFieldChange}
                  onBlur={formik.handleBlur}
                  error={errMsg(formik, "passport")}
                />
                <FormDateField
                  id="ci-passportExpiry"
                  name="passportExpiry"
                  label="Passport Expiry Date"
                  placeholder="Select Date"
                  required
                  value={values.passportExpiry}
                  onChange={function (iso) {
                    formik.setFieldValue("passportExpiry", iso);
                  }}
                  onBlur={formik.handleBlur}
                  error={errMsg(formik, "passportExpiry")}
                />
              </FormTwoCol>
            ) : null}

            <Box sx={{ mb: 1.5, display: "flex", alignItems: "center" }}>
              <FormCheckboxField
                name="hasPriorExperience"
                checked={Boolean(values.hasPriorExperience)}
                onChange={function (e) {
                  formik.setFieldValue(
                    "hasPriorExperience",
                    e.target.checked
                  );
                }}
                label="Do you have any prior work experience?"
              />
            </Box>

            {values.hasPriorExperience ? (
              <FormTwoCol>
                <Box>
                  <FormFieldLabel required>Total Experience</FormFieldLabel>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 1.5,
                    }}
                  >
                    <FormSelectField
                      id="ci-totalExperienceInYears"
                      name="totalExperienceInYears"
                      placeholder="Select Year"
                      options={EXPERIENCE_YEAR_OPTIONS}
                      value={values.totalExperienceInYears}
                      onChange={onFieldChange}
                      onBlur={formik.handleBlur}
                      error={errMsg(formik, "totalExperienceInYears")}
                    />
                    <FormSelectField
                      id="ci-totalExperienceInMonths"
                      name="totalExperienceInMonths"
                      placeholder="Select Month"
                      options={EXPERIENCE_MONTH_OPTIONS}
                      value={values.totalExperienceInMonths}
                      onChange={onFieldChange}
                      onBlur={formik.handleBlur}
                      error={errMsg(formik, "totalExperienceInMonths")}
                    />
                  </Box>
                </Box>
                <Box>
                  <FormFieldLabel required>Relevant Experience</FormFieldLabel>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 1.5,
                    }}
                  >
                    <FormSelectField
                      id="ci-relevantExperienceInYears"
                      name="relevantExperienceInYears"
                      placeholder="Select Year"
                      options={EXPERIENCE_YEAR_OPTIONS}
                      value={values.relevantExperienceInYears}
                      onChange={onFieldChange}
                      onBlur={formik.handleBlur}
                      error={errMsg(formik, "relevantExperienceInYears")}
                    />
                    <FormSelectField
                      id="ci-relevantExperienceInMonths"
                      name="relevantExperienceInMonths"
                      placeholder="Select Month"
                      options={EXPERIENCE_MONTH_OPTIONS}
                      value={values.relevantExperienceInMonths}
                      onChange={onFieldChange}
                      onBlur={formik.handleBlur}
                      error={errMsg(formik, "relevantExperienceInMonths")}
                    />
                  </Box>
                </Box>
              </FormTwoCol>
            ) : null}

                        <FormTwoCol>
              <FormTextField
                id="ci-linkedIn"
                rule="linkedIn"
                name="linkedIn"
                label="LinkedIn Profile URL"
                hint="Your public profile link, e.g. https://www.linkedin.com/in/your-name"
                value={values.linkedIn}
                onChange={onFieldChange}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "linkedIn")}
              />
              <FormTextField
                id="ci-pf"
                rule="pf"
                name="pf"
                label="PF Number"
                hint={FIELD_HINTS.pf}
                value={values.pf}
                onChange={onFieldChange}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "pf")}
                disabled={isHrLockedField(isAdmin, hrOnboardingSnapshot.pf)}
              />
            </FormTwoCol>

            <FormTwoCol>
              <FormTextField
                id="ci-uan"
                rule="uan"
                name="uan"
                label="UAN Number"
                hint={FIELD_HINTS.uan}
                value={values.uan}
                onChange={onFieldChange}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "uan")}
                disabled={isHrLockedField(isAdmin, hrOnboardingSnapshot.uan)}
              />
              <FormTextField
                id="ci-ESIC"
                rule="esic"
                name="ESIC"
                label="ESIC Number"
                hint={FIELD_HINTS.esic}
                value={values.ESIC}
                onChange={onFieldChange}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "ESIC")}
              />
            </FormTwoCol>

            <Box sx={{ mb: 2 }}>
              <FormFieldLabel required>Skills</FormFieldLabel>
              <Box
                id="ci-skills"
                sx={{
                  "& .basic-multi-select": {
                    fontFamily: JOINEE_FORM_FONT,
                    fontSize: 14,
                    fontWeight: 400,
                  },
                }}
              >
                <SelectSkills
                  skills={values.skills || []}
                  setUser={setSkillsUser}
                />
              </Box>
              {errMsg(formik, "skills") ? (
                <Typography
                  sx={{
                    fontFamily: JOINEE_FORM_FONT,
                    fontSize: 12,
                    color: JOINEE_FORM_COLORS.error,
                    mt: 0.5,
                  }}
                >
                  {errMsg(formik, "skills")}
                </Typography>
              ) : (
                <Box sx={{ height: 20 }} />
              )}
            </Box>

            <Divider sx={{ borderColor: JOINEE_FORM_COLORS.border, my: 2 }} />

            {/* 2. Current Address */}
            <FormSectionTitle>Current Address</FormSectionTitle>
            <FormTwoCol>
              <FormTextField
                id="ci-curr-line1"
                rule="address"
                name="currentAddress.addressLine1"
                label="Address Line 1"
                placeholder="Enter Address Line 1"
                required
                value={values.currentAddress.addressLine1}
                onChange={function (e) {
                  syncPermanentIfNeeded(
                    "currentAddress.addressLine1",
                    e.target.value
                  );
                }}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "currentAddress.addressLine1")}
              />
              <FormTextField
                id="ci-curr-line2"
                rule="address"
                name="currentAddress.addressLine2"
                label="Address Line 2"
                placeholder="Enter Address Line 2"
                value={values.currentAddress.addressLine2}
                onChange={function (e) {
                  syncPermanentIfNeeded(
                    "currentAddress.addressLine2",
                    e.target.value
                  );
                }}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "currentAddress.addressLine2")}
              />
            </FormTwoCol>
            <FormTwoCol>
              <FormTextField
                id="ci-curr-landmark"
                rule="address"
                name="currentAddress.landmark"
                label="Landmark"
                placeholder="Enter landmark"
                value={values.currentAddress.landmark}
                onChange={function (e) {
                  syncPermanentIfNeeded(
                    "currentAddress.landmark",
                    e.target.value
                  );
                }}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "currentAddress.landmark")}
              />
              <FormTextField
                id="ci-curr-postal"
                rule="postalCode"
                name="currentAddress.postalCode"
                label="Postal Code"
                required
                value={values.currentAddress.postalCode}
                onChange={function (e) {
                  syncPermanentIfNeeded(
                    "currentAddress.postalCode",
                    e.target.value
                  );
                }}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "currentAddress.postalCode")}
              />
            </FormTwoCol>
            <FormTwoCol>
              <FormTextField
                id="ci-curr-period"
                rule="years"
                name="currentAddress.periodOfStay"
                label="Period of Stay (Years)"
                placeholder="Enter years (e.g. 2)"
                required
                value={values.currentAddress.periodOfStay}
                onChange={function (e) {
                  syncPermanentIfNeeded(
                    "currentAddress.periodOfStay",
                    e.target.value
                  );
                }}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "currentAddress.periodOfStay")}
              />
              <FormSelectField
                id="ci-curr-residency"
                name="currentAddress.natureOfResidency"
                label="Nature of Residency"
                placeholder="Select Nature of Residency"
                required
                options={NATURE_OF_RESIDENCY}
                value={values.currentAddress.natureOfResidency}
                onChange={function (e) {
                  syncPermanentIfNeeded(
                    "currentAddress.natureOfResidency",
                    e.target.value
                  );
                }}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "currentAddress.natureOfResidency")}
              />
            </FormTwoCol>

            <Divider sx={{ borderColor: JOINEE_FORM_COLORS.border, my: 2 }} />

            {/* 3. Permanent Address */}
            <FormSectionTitle
              action={
                <FormCheckboxField
                  name="permanentAddressSame"
                  checked={Boolean(values.permanentAddressSame)}
                  onChange={onSameAddressChange}
                  label="Same as Current Address"
                  dense
                />
              }
            >
              Permanent Address
            </FormSectionTitle>
            <FormTwoCol>
              <FormTextField
                id="ci-perm-line1"
                rule="address"
                name="permanentAddress.addressLine1"
                label="Address Line 1"
                placeholder="Enter Address Line 1"
                required={!values.permanentAddressSame}
                disabled={values.permanentAddressSame}
                value={values.permanentAddress.addressLine1}
                onChange={onFieldChange}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "permanentAddress.addressLine1")}
              />
              <FormTextField
                id="ci-perm-line2"
                rule="address"
                name="permanentAddress.addressLine2"
                label="Address Line 2"
                placeholder="Enter Address Line 2"
                disabled={values.permanentAddressSame}
                value={values.permanentAddress.addressLine2}
                onChange={onFieldChange}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "permanentAddress.addressLine2")}
              />
            </FormTwoCol>
            <FormTwoCol>
              <FormTextField
                id="ci-perm-landmark"
                rule="address"
                name="permanentAddress.landmark"
                label="Landmark"
                placeholder="Enter landmark"
                disabled={values.permanentAddressSame}
                value={values.permanentAddress.landmark}
                onChange={onFieldChange}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "permanentAddress.landmark")}
              />
              <FormTextField
                id="ci-perm-postal"
                rule="postalCode"
                name="permanentAddress.postalCode"
                label="Postal Code"
                required={!values.permanentAddressSame}
                disabled={values.permanentAddressSame}
                value={values.permanentAddress.postalCode}
                onChange={onFieldChange}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "permanentAddress.postalCode")}
              />
            </FormTwoCol>
            <FormTwoCol>
              <FormTextField
                id="ci-perm-period"
                rule="years"
                name="permanentAddress.periodOfStay"
                label="Period of Stay (Years)"
                placeholder="Enter years (e.g. 2)"
                required={!values.permanentAddressSame}
                disabled={values.permanentAddressSame}
                value={values.permanentAddress.periodOfStay}
                onChange={onFieldChange}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "permanentAddress.periodOfStay")}
              />
              <FormSelectField
                id="ci-perm-residency"
                name="permanentAddress.natureOfResidency"
                label="Nature of Residency"
                placeholder="Select Nature of Residency"
                required={!values.permanentAddressSame}
                disabled={values.permanentAddressSame}
                options={NATURE_OF_RESIDENCY}
                value={values.permanentAddress.natureOfResidency}
                onChange={onFieldChange}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "permanentAddress.natureOfResidency")}
              />
            </FormTwoCol>

            <Divider sx={{ borderColor: JOINEE_FORM_COLORS.border, my: 2 }} />

            {/* 4. Family Details */}
            <FormSectionTitle
              action={
                <FormAddButton type="button" onClick={addFamilyRow}>
                  + Add Member
                </FormAddButton>
              }
            >
              Add Family Details
            </FormSectionTitle>
            {(values.familyDetails || []).map(function (row, index) {
              return (
                <Box key={"family-" + index} sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "1fr 1fr 1fr 40px",
                      },
                      gap: { xs: 1.5, md: 2 },
                      alignItems: "start",
                    }}
                  >
                    <FormTextField
                      id={"ci-family-name-" + index}
                      rule="personName"
                      name={"familyDetails." + index + ".name"}
                      label={index === 0 ? "Name" : ""}
                      placeholder="Enter Full Name"
                      required
                      value={row.name}
                      onChange={onFieldChange}
                      onBlur={formik.handleBlur}
                      error={errMsg(formik, "familyDetails." + index + ".name")}
                    />
                    <FormTextField
                      id={"ci-family-contact-" + index}
                      rule="mobile"
                      name={"familyDetails." + index + ".contact"}
                      label={index === 0 ? "Phone Number" : ""}
                      required
                      value={row.contact}
                      onChange={onFieldChange}
                      onBlur={formik.handleBlur}
                      error={errMsg(
                        formik,
                        "familyDetails." + index + ".contact"
                      )}
                    />
                    <FormSelectField
                      id={"ci-family-relation-" + index}
                      name={"familyDetails." + index + ".relationship"}
                      label={index === 0 ? "Select Relation" : ""}
                      placeholder="Select Relation"
                      required
                      options={RELATION_OPTIONS}
                      value={row.relationship}
                      onChange={onFieldChange}
                      onBlur={formik.handleBlur}
                      error={errMsg(
                        formik,
                        "familyDetails." + index + ".relationship"
                      )}
                    />
                    {values.familyDetails.length > 1 ? (
                      <RowRemoveButton
                        aria-label="Remove family member"
                        showLabelSpacer={index === 0}
                        onClick={function () {
                          removeFamilyRow(index);
                        }}
                      />
                    ) : (
                      <Box sx={{ width: 40 }} />
                    )}
                  </Box>
                </Box>
              );
            })}

            <Divider sx={{ borderColor: JOINEE_FORM_COLORS.border, my: 2 }} />

            {/* 5. Emergency Contact */}
            <FormSectionTitle
              action={
                <FormAddButton type="button" onClick={addEmergencyRow}>
                  + Add Emergency Contact
                </FormAddButton>
              }
            >
              Emergency Contact
            </FormSectionTitle>
            {(values.emergencyContact || []).map(function (row, index) {
              return (
                <Box key={"emergency-" + index} sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "1fr 1fr 1fr 40px",
                      },
                      gap: { xs: 1.5, md: 2 },
                      alignItems: "start",
                    }}
                  >
                    <FormTextField
                      id={"ci-emergency-name-" + index}
                      rule="personName"
                      name={"emergencyContact." + index + ".name"}
                      label={index === 0 ? "Name" : ""}
                      placeholder="Enter Full Name"
                      required
                      value={row.name}
                      onChange={onFieldChange}
                      onBlur={formik.handleBlur}
                      error={errMsg(
                        formik,
                        "emergencyContact." + index + ".name"
                      )}
                    />
                    <FormTextField
                      id={"ci-emergency-contact-" + index}
                      rule="mobile"
                      name={"emergencyContact." + index + ".contact"}
                      label={index === 0 ? "Phone Number" : ""}
                      required
                      value={row.contact}
                      onChange={onFieldChange}
                      onBlur={formik.handleBlur}
                      error={errMsg(
                        formik,
                        "emergencyContact." + index + ".contact"
                      )}
                    />
                    <FormSelectField
                      id={"ci-emergency-relation-" + index}
                      name={"emergencyContact." + index + ".relationship"}
                      label={index === 0 ? "Select Relation" : ""}
                      placeholder="Select Relation"
                      required
                      options={RELATION_OPTIONS}
                      value={row.relationship}
                      onChange={onFieldChange}
                      onBlur={formik.handleBlur}
                      error={errMsg(
                        formik,
                        "emergencyContact." + index + ".relationship"
                      )}
                    />
                    {values.emergencyContact.length > 1 ? (
                      <RowRemoveButton
                        aria-label="Remove emergency contact"
                        showLabelSpacer={index === 0}
                        onClick={function () {
                          removeEmergencyRow(index);
                        }}
                      />
                    ) : (
                      <Box sx={{ width: 40 }} />
                    )}
                  </Box>
                </Box>
              );
            })}

            <Divider sx={{ borderColor: JOINEE_FORM_COLORS.border, my: 2 }} />

            {/* 6. Food Coupon */}
            <FormSectionTitle>Food Coupon</FormSectionTitle>
            <FormTwoCol>
              <FormSelectField
                id="ci-sodexoCouponValue"
                name="sodexoCouponValue"
                label="Food Coupon Value"
                placeholder="Select Coupon Value"
                options={FOOD_COUPON_OPTIONS}
                value={values.sodexoCouponValue}
                onChange={onFieldChange}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "sodexoCouponValue")}
              />
              <Box />
            </FormTwoCol>

            <Divider sx={{ borderColor: JOINEE_FORM_COLORS.border, my: 2 }} />

            {/* 7. Signature */}
            <FormSectionTitle>Signature</FormSectionTitle>
            <Box
              sx={{
                width: { xs: "100%", sm: 338 },
                maxWidth: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                mb: 2,
              }}
            >
              <Box
                onClick={function () {
                  if (values.signature) {
                    setSignaturePreviewOpen(true);
                  }
                }}
                sx={{
                  width: "100%",
                  height: 149,
                  borderRadius: "6px",
                  bgcolor: "#EDEDED",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  cursor: values.signature ? "zoom-in" : "default",
                  pointerEvents: values.signature ? "auto" : undefined,
                  border: values.signature
                    ? "1px solid " + JOINEE_FORM_COLORS.border
                    : "none",
                }}
              >
                {values.signature ? (
                  <Box
                    component="img"
                    src={values.signature}
                    alt="Signature preview"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <Box
                    component="img"
                    src={signaturePlaceholder}
                    alt=""
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                )}
              </Box>
              {values.signature ? (
                <Typography
                  sx={{
                    fontFamily: JOINEE_FORM_FONT,
                    fontSize: 12,
                    fontWeight: 400,
                    color: "#848F9A",
                    mt: -0.5,
                  }}
                >
                  {values.signature_fileName
                    ? values.signature_fileName
                    : "Signature uploaded"}{" "}
                  — click image to view full size
                </Typography>
              ) : null}
              {errMsg(formik, "signature") ? (
                <Typography
                  sx={{
                    fontFamily: JOINEE_FORM_FONT,
                    fontSize: 12,
                    color: JOINEE_FORM_COLORS.error,
                  }}
                >
                  {errMsg(formik, "signature")}
                </Typography>
              ) : null}
              {readOnly ? (
                <MediaViewActions
                  src={values.signature}
                  fileName={values.signature_fileName}
                  fallbackName="signature"
                  onView={function () {
                    setSignaturePreviewOpen(true);
                  }}
                />
              ) : (
              <>
              <input
                ref={signatureInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={onSignatureFile}
              />
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={function () {
                    if (signatureInputRef.current) {
                      signatureInputRef.current.click();
                    }
                  }}
                  sx={{
                    flex: 1,
                    minWidth: 120,
                    fontFamily: JOINEE_FORM_FONT,
                    fontSize: 14,
                    fontWeight: 400,
                    textTransform: "none",
                    color: "#171A1C",
                    borderColor: JOINEE_FORM_COLORS.border,
                    borderRadius: "6px",
                    minHeight: 32,
                    bgcolor: JOINEE_FORM_COLORS.surface,
                    "&:hover": {
                      borderColor: JOINEE_FORM_COLORS.navy,
                      bgcolor: "#F5F7FF",
                    },
                  }}
                >
                  {values.signature ? "Replace Signature" : "Upload Signature"}
                </Button>
                {values.signature ? (
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={function () {
                      setSignaturePreviewOpen(true);
                    }}
                    sx={{
                      fontFamily: JOINEE_FORM_FONT,
                      fontSize: 14,
                      fontWeight: 400,
                      textTransform: "none",
                      color: JOINEE_FORM_COLORS.primary,
                      borderColor: JOINEE_FORM_COLORS.primary,
                      borderRadius: "6px",
                      minHeight: 32,
                      px: 2,
                    }}
                  >
                    View
                  </Button>
                ) : null}
              </Box>
              </>
              )}
            </Box>

            <MediaPreviewDialog
              open={signaturePreviewOpen}
              onClose={function () {
                setSignaturePreviewOpen(false);
              }}
              src={values.signature}
              fileName={values.signature_fileName}
              fallbackName="signature"
              title="Signature"
            />

            <Box sx={{ mt: 1, mb: 1 }}>
              <FormCheckboxField
                name="signatureLegalConsent"
                checked={Boolean(values.signatureLegalConsent)}
                onChange={function (e) {
                  var checked = e.target.checked;
                  formik.setFieldValue("signatureLegalConsent", checked, true);
                  formik.setFieldValue(
                    "signatureLegalConsentAt",
                    checked
                      ? values.signatureLegalConsentAt ||
                          new Date().toISOString()
                      : null
                  );
                  formik.setFieldTouched("signatureLegalConsent", true, false);
                  if (checked) {
                    formik.setFieldError("signatureLegalConsent", undefined);
                  }
                }}
                label={SIGNATURE_LEGAL_TEXT}
                legal
              />
              {errMsg(formik, "signatureLegalConsent") &&
              !values.signatureLegalConsent ? (
                <Typography
                  sx={{
                    fontFamily: JOINEE_FORM_FONT,
                    fontSize: 12,
                    color: JOINEE_FORM_COLORS.error,
                    mt: 0.5,
                    ml: "28px",
                  }}
                >
                  {errMsg(formik, "signatureLegalConsent")}
                </Typography>
              ) : null}
            </Box>
            </Box>

            {!hideInternalActions ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1.5,
                  mt: 3,
                  flexWrap: "wrap",
                }}
              >
                {!isAdmin ? (
                  <Button
                    type="button"
                    variant="outlined"
                    disabled={formik.isSubmitting}
                    onClick={function () {
                      handleSaveDraft(formik);
                    }}
                    sx={{
                      fontFamily: JOINEE_FORM_FONT,
                      fontSize: 14,
                      fontWeight: 400,
                      textTransform: "none",
                      color: JOINEE_FORM_COLORS.primary,
                      borderColor: JOINEE_FORM_COLORS.primary,
                      borderRadius: "6px",
                      minHeight: 36,
                      px: 2,
                    }}
                  >
                    Save as draft
                  </Button>
                ) : null}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={formik.isSubmitting}
                  sx={{
                    fontFamily: JOINEE_FORM_FONT,
                    fontSize: 14,
                    fontWeight: 400,
                    textTransform: "none",
                    bgcolor: JOINEE_FORM_COLORS.primary,
                    borderRadius: "6px",
                    minHeight: 36,
                    px: 2.5,
                    boxShadow: "none",
                    "&:hover": { bgcolor: JOINEE_FORM_COLORS.navy },
                  }}
                >
                  Save and next
                </Button>
              </Box>
            ) : null}
          </Form>
        );
      }}
    </Formik>
  );
}

export default CandidateInfo;
